import { useState, useEffect } from 'react';
import { Global, css } from '@emotion/react';
import {
  DayPicker,
  DayPickerDefaultProps,
  DayModifiers,
  Matcher,
} from 'react-day-picker';
import { isBefore } from 'date-fns/isBefore';
import { isAfter } from 'date-fns/isAfter';
import ArrowLast from '@engineering/icons/arrow-last';

import type { DateRange } from './types';

type Props = {
  from?: Date;
  to?: Date;
  onDatesChange?: (dateRange: DateRange) => void;
  scrollToSelectedMonth?: boolean;
};

/**
These global styles required by the `react-day-picker` code are applied using
`styled-jsx` to add them only when this component is used. This also fixes
missing styles in Storybook. Adding them to Storybook using a CSS file via
"import" unfortunately does not work with Tailwind’s `theme()` function.
*/
const globalStyles = css`
  .rdp {
    position: relative;
    outline: 0;
    color: #222222;
    user-select: none;
  }

  .rdp-button_reset {
    margin: 0;
    padding: 0;
    border: 0;
    font: inherit;
    background: none;
    appearance: none;
  }

  .rdp-months {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }

  .rdp-month {
    margin: 1.5rem 1rem 1rem;
  }

  .rdp-nav_button {
    position: absolute;
    top: 1.5rem;
    width: 36px;
    height: 36px;
    outline: 0;
    color: inherit;
    -webkit-tap-highlight-color: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      cursor: not-allowed;
    }
  }

  .rdp-nav_button.rdp-nav_button_previous {
    left: 0.75rem;
  }

  .rdp-nav_button.rdp-nav_button_next {
    right: 0.75rem;
    transform: rotate(0.5turn);
  }

  .rdp-caption_label {
    margin: 0.25rem 0;
    font-size: 1.125rem;
    font-weight: 600;
    text-align: center;
    text-transform: capitalize;
  }

  .rdp-table {
    border-spacing: 0;
    border-collapse: collapse;
  }

  .rdp-head_cell {
    display: table-cell;
    padding: 8px 0;
    color: #b6bfc1;
    font-size: 0.938rem;
    font-weight: 600;
    text-align: center;
  }

  .rdp-cell {
    padding: 8px 0 0;
  }

  .rdp-day {
    outline: 0;
    width: 40px;
    height: 32px;
    font-size: 1.125rem;
    text-align: center;
    vertical-align: middle;
    cursor: pointer;
    color: #222222;
  }

  .rdp-day.rdp-day_outside {
    color: #6ec2b7;
    cursor: default;
  }

  .rdp-day.rdp-day_disabled {
    color: #b6bfc1;
    cursor: default;
  }

  /* selected pick-up/drop-off dates */
  .rdp-day.rdp-day_selected:not(.rdp-day_disabled, .rdp-day_outside) {
    position: relative;
    color: #FFFFFF;
  }

  .rdp-day.rdp-day_range_start.rdp-day_selected:not(
      .rdp-day_disabled,
      .rdp-day_outside
    ) {
    background-image: radial-gradient(
        circle at center,
        #00977D,
        #00977D 60%,
        transparent 65%
      ),
      linear-gradient(
        to right,
        transparent,
        transparent 49%,
        #C5E7E2} 50%
      );
  }

  .rdp-day.rdp-day_range_end.rdp-day_selected:not(
      .rdp-day_disabled,
      .rdp-day_outside
    ) {
    background-image: radial-gradient(
        circle at center,
        #00977D,
        #00977D 60%,
        transparent 65%
      ),
      linear-gradient(
        to left,
        transparent,
        transparent 49%,
        #C5E7E2} 50%
      );
  }

  .rdp-day.rdp-day_range_start.rdp-day_range_end.rdp-day_selected:not(
      .rdp-day_disabled,
      .rdp-day_outside
    ) {
    background-image: radial-gradient(
      circle at center,
      #00977D,
      #00977D 70%,
      transparent 71%
    );
  }

  /* range between pick-up and drop-off */
  .rdp-day.rdp-day_selected:not(
      .rdp-day_range_start,
      .rdp-day_range_end,
      .rdp-day_outside
    ) {
    color: #222222;
    background-color: #C5E7E2};
  }

  /* today but not selected */
  .rdp-day.rdp-day_today:not(.rdp-day_selected) {
    margin: 0 0.25rem;
    width: 32px;
    border: 1px solid #b6bfc1;
    border-radius: 100%;
  }

  .rdp-vhidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  @media screen and (min-width: 1280px) {
    .rdp-months {
      justify-content: space-around;
    }
  }
`;

const NavigationIcon = () => (
  <ArrowLast className="hover:fill-primary/80 w-50 h-50" />
);

const DatePicker = ({
  from,
  to,
  scrollToSelectedMonth = false,
  onDatesChange,
  ...props
}: Props & DayPickerDefaultProps) => {
  useEffect(() => {
    if (!scrollToSelectedMonth) {
      return;
    }
    const selectedDay = document.querySelector('.rdp-day_selected');
    const selectedMonthElm =
      selectedDay?.parentElement?.parentElement?.parentElement?.parentElement
        ?.parentElement;
    if (selectedMonthElm) {
      selectedMonthElm.scrollIntoView();
    }
  }, [scrollToSelectedMonth]);

  const [selectedFrom, setFrom] = useState(from || null);
  const [selectedTo, setTo] = useState(to || null);
  const [hoveredTo, setHoveredTo] = useState(undefined as undefined | Date);

  const isSelectingFirstDay = (date: Date) => {
    const isBeforeFirstDay = selectedFrom && isBefore(date, selectedFrom);
    const isRangeSelected = selectedFrom && selectedTo;
    return !selectedFrom || isBeforeFirstDay || isRangeSelected;
  };

  const handleOnDayClick = (date: Date, modifiers: DayModifiers) => {
    if (modifiers.disabled) {
      return;
    }

    if (isSelectingFirstDay(date)) {
      setFrom(date);
      setTo(null);
      setHoveredTo(undefined);
    } else {
      setTo(date);
      setHoveredTo(date);
      if (typeof onDatesChange !== 'undefined') {
        onDatesChange({ from: selectedFrom, to: date });
      }
    }
  };

  const getHighlightedDays = () => {
    const highlightRules: Matcher[] = [
      (day: Date) => {
        if (selectedFrom && selectedTo) {
          return isAfter(day, selectedFrom) && isBefore(day, selectedTo);
        }
        return false;
      },
    ];

    if (selectedFrom) {
      highlightRules.push(selectedFrom);
    }

    if (selectedFrom && hoveredTo) {
      highlightRules.push({ from: selectedFrom, to: hoveredTo });
    }

    if (selectedFrom && selectedTo) {
      highlightRules.push({ from: selectedFrom, to: selectedTo });
    }

    return highlightRules;
  };

  const getModifiers = () => {
    let modifiers: DayModifiers = {};
    if (selectedFrom) {
      modifiers = { ...modifiers, from: [selectedFrom] };
    }
    if (hoveredTo) {
      modifiers = { ...modifiers, to: hoveredTo };
    } else if (selectedTo) {
      modifiers = { ...modifiers, to: selectedTo };
    }
    return modifiers;
  };

  const handleDayMouseEnter = (day: Date) => {
    if ((from && isBefore(day, from)) || isSelectingFirstDay(day)) {
      return;
    }
    setHoveredTo(day);
  };

  return (
    <>
      <Global styles={globalStyles} />
      <DayPicker
        components={{ IconLeft: NavigationIcon, IconRight: NavigationIcon }}
        onDayClick={handleOnDayClick}
        onDayMouseEnter={handleDayMouseEnter}
        selected={getHighlightedDays()}
        modifiers={getModifiers()}
        disabled={{ before: new Date() }}
        modifiersClassNames={{
          outside: 'rdp-day_outside',
          disabled: 'rdp-day_disabled',
          today: 'rdp-day_today',
          from: 'rdp-day_range_start',
          to: 'rdp-day_range_end',
        }}
        {...props}
      />
    </>
  );
};

export { DatePicker };
